import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import BetbuilderActions from '../../bet-builder/actions';
import BetslipComboBetActions from '../../combo-bet/actions';
import { ensureLinearMode } from '../../linear/helpers';
import BetslipPickActions from '../../picks/actions';
import { addGroup, replaceGroup } from '../../picks/grouped-picks.actions';
import BetslipSingleBetActions from '../../single-bet/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { BetslipTypesActions } from '../actions';
import { selectTypesPickCount } from '../selectors';

@Injectable()
export default class LinearTypeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betslipConfig: BetslipConfig,
    ) {}

    switchLinearTypes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipPickActions.replacePick,
                BetslipPickActions.removePick,
                BetslipPickActions.removeMultiplePicks,
                BetslipPickActions.updateV1Picks,
                BetslipPickActions.updateV2Picks,
                BetslipPickActions.updateGroupPicks,
                replaceGroup,
                addGroup,
                BetslipPickActions.removeTypePicks,
                BetslipActions.toggleBetslipDisplayMode,
                BetslipTeaserActions.addUpdatePick,
                BetslipComboBetActions.removePick,
                BetslipComboBetActions.addPick,
                BetslipSingleBetActions.addPick,
                BetslipSingleBetActions.removePick,
                BetslipSystemBetActions.addPick,
                BetslipSystemBetActions.removePick,
                BetslipTeaserActions.addUpdatePick,
                BetslipTeaserActions.removePick,
                BetslipTeaserActions.removePicks,
                BetbuilderActions.removePick,
                BetbuilderActions.addPick,
            ),
            ensureLinearMode(this.store),
            concatLatestFrom(() => this.store.select(selectTypesPickCount)),
            map(([, { singlePicksCount, comboPicksCount, systemPicksCount, betBuilderPicksCount, teaserPicksCount }]) => {
                const isComboActive = this.isComboActive(comboPicksCount, betBuilderPicksCount);
                const isSystemActive = this.isSystemActive(systemPicksCount);
                const isTeaserActive = this.isTeaserActive(teaserPicksCount);

                return BetslipTypesActions.setLinearTypes({
                    types: {
                        [BetslipType.Single]: singlePicksCount > 0,
                        [BetslipType.Combo]: isComboActive,
                        [BetslipType.System]: isSystemActive,
                        [BetslipType.BetBuilder]: betBuilderPicksCount > 0,
                        [BetslipType.Teaser]: isTeaserActive,
                    },
                });
            }),
        );
    });

    private isComboActive(picksCount: number, betBuilderPicksCount: number): boolean {
        return (
            picksCount >= this.betslipConfig.minComboBets || (!this.betslipConfig.isLinearSeparateBetBuilderModuleEnabled && betBuilderPicksCount > 0)
        );
    }

    private isSystemActive(picksCount: number): boolean {
        return picksCount >= this.betslipConfig.minSystemBets;
    }

    private isTeaserActive(picksCount: number): boolean {
        return picksCount >= this.betslipConfig.minTeaserBets;
    }
}
